import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type ComponentType,
  type Dispatch,
  type SetStateAction
} from 'react';
import { Formik, type FormikHelpers } from 'formik';
import { Alert, Button, Card, Col, Form, Row, Spinner } from 'react-bootstrap';
import { array, object, string } from 'yup';
import { clusteringHistoryColumnConfig } from '@/app/KeywordResearch/config/clusteringHistoryColumnConfig';
import {
  useCreateClusteringMutation,
  useGetClusteringHistoryQuery
} from '@/app/KeywordResearch/api/clusteringApi';
import { useNavigate, useOutletContext } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { ToolAccessDenied } from '@/app/common/components/ToolAccessGuard';
import useToolAccess from '@/app/common/hooks/useToolAccess';
import { keywordResearchTooltips } from '@/app/KeywordResearch/tooltip/keywordResearchTooltips';
import AgGridWrapper from '@/app/common/components/ag-grid/AgGridWrapper';
import AgGridSkeleton from '@/app/common/components/skeleton/AgGridSkeleton';
import ClusteringFormSkeleton from '../components/keyword-clustring/components/ClusteringFormSkeleton';
import numberHumanizer from '@/helpers/numberHumanizer';
import HelpTooltip from '@/app/common/components/HelpTooltip';
import DataLoadError from '@/components/errors/DataLoadError';
import { getActiveQueryError } from '@/app/common/utils/apiErrorUtils';
import classNames from 'classnames';
import ClusteringModeSelector from '../components/keyword-clustring/ClusteringModeSelector';
import ChildrensTooltip from '@/components/common/ChildrensTooltip';
import { errorMessage } from '@/utils/toast';
import useDomain from '@/hooks/useDomain';
import { useGetTariffInfoQuery } from '@/app/KeywordTracker/api/keywordsApi';
import { useGetWalletBalanceQuery } from '@/app/Wallet/api/walletApi';
import { useGetAiUserSettingsQuery } from '@/app/Settings/api/aiSettingsApi';
import AiNoActiveApiKeyAlert from '@/app/common/components/AiNoActiveApiKeyAlert';
import AiFormWarningAlert from '@/app/common/components/AiFormWarningAlert';
import { AI_WALLET_INSUFFICIENT_CODE } from '@/app/common/components/AiInsufficientBalanceModal';
import {
  AI_NO_ACTIVE_PROVIDER_KEY_CODE,
  getAiWarningMessage,
  resolveAiFormAlertMessage
} from '@/app/common/validators/aiMessages';

const LIMIT_CLUSTERING_KEYWORDS = 2000;
const MIN_CLUSTERING_KEYWORDS = 50;
const MIN_AI_CLUSTERING_BALANCE = 100_000;
const EMPTY_PROVIDER_KEYS: Array<{ is_active: boolean }> = [];

const countKeywords = (words?: Array<string | undefined | null> | null) =>
  (words ?? [])
    .map(word => (word ?? '').trim())
    .filter(word => word !== '' && word !== 'other').length;

type BreadcrumbItem = { text: string; href: string };
type OutletContext = [
  BreadcrumbItem[],
  Dispatch<SetStateAction<BreadcrumbItem[]>>
];

type ClusteringFormValues = {
  words: string[];
};

type ClusteringMode = 'herachical' | 'ai' | string;

type AgGridWrapperProps = {
  settings?: Record<string, unknown>;
  height: string;
  columns: unknown;
  data?: unknown[] | null;
};

type AgGridSkeletonProps = {
  height: string;
  settings?: Record<string, unknown>;
};

type ClusteringModeSelectorProps = {
  mode: ClusteringMode;
  onChange: (mode: ClusteringMode) => void;
};

const TypedAgGridWrapper = AgGridWrapper as ComponentType<AgGridWrapperProps>;
const TypedAgGridSkeleton = AgGridSkeleton as ComponentType<AgGridSkeletonProps>;
const TypedClusteringModeSelector =
  ClusteringModeSelector as ComponentType<ClusteringModeSelectorProps>;

const Clustering = () => {
  const gridRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const [mode, setMode] = useState<ClusteringMode>('herachical');
  const [aiConsent, setAiConsent] = useState(false);
  const [showAiConsentError, setShowAiConsentError] = useState(false);
  const [showNoActiveApiKeyAlert, setShowNoActiveApiKeyAlert] = useState(false);
  const [formWarningMessage, setFormWarningMessage] = useState<string | null>(
    null
  );
  const [, setBreadcrumbData] = useOutletContext<OutletContext>();
  const { domain_slug } = useDomain();
  const access = useToolAccess(null);
  const { data, isLoading, isError, error, refetch, isFetching } =
    useGetClusteringHistoryQuery(undefined, {
      skip: access.isLoading || !access.haveAccess
    });

  const { data: aiUserSettings, isFetching: isFetchingAiSettings } =
    useGetAiUserSettingsQuery(
      {},
      {
        skip: mode !== 'ai' || access.isLoading || !access.haveAccess,
        refetchOnMountOrArgChange: true
      }
    );

  const aiSettingsFlags = aiUserSettings?.settings ?? null;
  const providerKeys = aiUserSettings?.provider_keys ?? EMPTY_PROVIDER_KEYS;
  const hasLoadedAiSettings = aiUserSettings !== undefined;
  const hasActiveProviderKey = useMemo(
    () => providerKeys.some(key => key.is_active),
    [providerKeys]
  );

  /** Personal AI mode without active key — block before request (same as AiModal). */
  const needsApiKeySetup = useMemo(
    () =>
      hasLoadedAiSettings &&
      Boolean(aiSettingsFlags?.enable_user_ai) &&
      !hasActiveProviderKey,
    [hasLoadedAiSettings, aiSettingsFlags?.enable_user_ai, hasActiveProviderKey]
  );

  /** settings=null → default Binesh wallet AI. */
  const canUseAi = useMemo(() => {
    if (!hasLoadedAiSettings || aiSettingsFlags == null) return true;
    return (
      Boolean(aiSettingsFlags.enable_binesh_ai) ||
      (Boolean(aiSettingsFlags.enable_user_ai) && hasActiveProviderKey)
    );
  }, [hasLoadedAiSettings, aiSettingsFlags, hasActiveProviderKey]);

  const usesBineshWallet =
    !hasLoadedAiSettings ||
    aiSettingsFlags == null ||
    Boolean(aiSettingsFlags.enable_binesh_ai);

  const {
    data: walletBalanceResponse,
    isLoading: isLoadingWalletBalance,
    isFetching: isFetchingWalletBalance,
    isError: isWalletBalanceError
  } = useGetWalletBalanceQuery(
    { domain_slug: domain_slug ?? '' },
    {
      skip:
        mode !== 'ai' || !domain_slug || !usesBineshWallet || needsApiKeySetup
    }
  );

  const aiBalance = walletBalanceResponse?.aiBalance ?? 0;
  const isCheckingWalletBalance =
    mode === 'ai' &&
    usesBineshWallet &&
    !needsApiKeySetup &&
    walletBalanceResponse == null &&
    (isLoadingWalletBalance || isFetchingWalletBalance);
  const hasEnoughAiBalance =
    !usesBineshWallet ||
    (!isWalletBalanceError &&
      walletBalanceResponse != null &&
      aiBalance >= MIN_AI_CLUSTERING_BALANCE);
  const isAiConsentRequired =
    mode === 'ai' && usesBineshWallet && hasEnoughAiBalance && !needsApiKeySetup;
  const isAiModeBlocked =
    mode === 'ai' &&
    (isFetchingAiSettings ||
      needsApiKeySetup ||
      !canUseAi ||
      isCheckingWalletBalance ||
      (usesBineshWallet && (isWalletBalanceError || !hasEnoughAiBalance)));

  useEffect(() => {
    setBreadcrumbData([{ text: 'خوشه بندی', href: '#' }]);
  }, [setBreadcrumbData]);

  useEffect(() => {
    if (mode !== 'ai' || !isAiConsentRequired) {
      setAiConsent(false);
      setShowAiConsentError(false);
    }
  }, [mode, isAiConsentRequired]);

  useEffect(() => {
    if (mode === 'ai' && needsApiKeySetup) {
      setShowNoActiveApiKeyAlert(true);
    } else if (mode !== 'ai') {
      setShowNoActiveApiKeyAlert(false);
    }
  }, [mode, needsApiKeySetup]);

  const [createClustering, { isLoading: isCreating }] =
    useCreateClusteringMutation();

  const handleModeChange = (nextMode: ClusteringMode) => {
    setMode(nextMode);
    if (nextMode !== 'ai') {
      setAiConsent(false);
      setShowAiConsentError(false);
      setShowNoActiveApiKeyAlert(false);
    }
  };

  const handleAiConsentChange = (checked: boolean) => {
    setAiConsent(checked);
    if (checked) {
      setShowAiConsentError(false);
    }
  };

  const onSubmit = async (
    values: ClusteringFormValues,
    _actions: FormikHelpers<ClusteringFormValues>
  ) => {
    const keywords = values.words
      .map(word => word.trim())
      .filter(word => word !== '' && word !== 'other');

    if (keywords.length < MIN_CLUSTERING_KEYWORDS) {
      errorMessage('حداقل باید ۵۰ کلمه وارد بشه.');
      return;
    }

    if (keywords.length > LIMIT_CLUSTERING_KEYWORDS) {
      errorMessage('تعداد کلمات بیش از حد مجاز است.');
      return;
    }

    if (mode === 'ai') {
      if (isFetchingAiSettings || !hasLoadedAiSettings) {
        errorMessage('در حال بارگذاری تنظیمات AI هستیم.');
        return;
      }
      if (needsApiKeySetup) {
        setShowNoActiveApiKeyAlert(true);
        return;
      }
      if (!canUseAi) {
        errorMessage(
          'برای خوشه‌بندی با AI، حالت «AI خودکار (کیف پول)» یا کلید API شخصی فعال را در تنظیمات AI انتخاب کنید.'
        );
        return;
      }
      if (usesBineshWallet) {
        if (isCheckingWalletBalance) {
          errorMessage('در حال بررسی موجودی کیف پول AI هستیم.');
          return;
        }
        if (isWalletBalanceError || !hasEnoughAiBalance) {
          errorMessage(
            'برای استفاده از خوشه‌بندی با AI، کیف پول AI شما باید حداقل ۱۰۰ هزار تومان شارژ داشته باشد.'
          );
          return;
        }
        if (!aiConsent) {
          setShowAiConsentError(true);
          return;
        }
        setShowAiConsentError(false);
      }
    }

    setShowNoActiveApiKeyAlert(false);
    setFormWarningMessage(null);
    try {
      const response = await createClustering({
        keywords,
        type_clustering: mode === 'ai' ? 'ai_clustering' : 'hierarchical',
        limit_count: LIMIT_CLUSTERING_KEYWORDS,
        domain_slug
      }).unwrap();

      if (response?.code === AI_NO_ACTIVE_PROVIDER_KEY_CODE) {
        setShowNoActiveApiKeyAlert(true);
        return;
      }
      if (response?.code === AI_WALLET_INSUFFICIENT_CODE) {
        errorMessage(
          getAiWarningMessage(AI_WALLET_INSUFFICIENT_CODE) ||
            'موجودی کیف پول کافی نیست.'
        );
        return;
      }

      if (response?.code === 200 && response?.data?.task_id) {
        navigate(response.data.task_id);
        return;
      }

      setFormWarningMessage(
        resolveAiFormAlertMessage(
          response?.code,
          response?.msg,
          'خطای غیر منتظره رخ داده است.'
        )
      );
    } catch (err) {
      console.log('Error on clustering:', err);
      const e = err as { data?: { code?: number; msg?: string } };
      if (e?.data?.code === AI_NO_ACTIVE_PROVIDER_KEY_CODE) {
        setShowNoActiveApiKeyAlert(true);
        return;
      }
      if (e?.data?.code === AI_WALLET_INSUFFICIENT_CODE) {
        errorMessage(
          getAiWarningMessage(AI_WALLET_INSUFFICIENT_CODE) ||
            'موجودی کیف پول کافی نیست.'
        );
        return;
      }
      setFormWarningMessage(
        resolveAiFormAlertMessage(
          e?.data?.code,
          e?.data?.msg,
          'خطای غیر منتظره رخ داده است.'
        )
      );
    }
  };
  const { data: trackerInfo, isFetching: isFetchingTrackerInfo } =
    useGetTariffInfoQuery(domain_slug);
  const isAccessAllowed = () =>
    trackerInfo?.data?.access_level !== 3 && !isFetchingTrackerInfo;

  if (!access.isLoading && !access.haveAccess) {
    return <ToolAccessDenied access={access} tool_label="ابزار خوشه بندی" />;
  }

  const queryError = getActiveQueryError([
    { isError, error, refetch, isFetching },
    {
      isError: access.isError,
      error: access.error,
      refetch: access.refetch,
      isFetching: access.isFetching
    }
  ]);

  if (queryError) return <DataLoadError {...queryError} />;

  const isPageLoading = access.isLoading || isLoading;
  const tableSkeleton = (
    <TypedAgGridSkeleton
      settings={{
        pagination: false,
        fullscreen: false,
        changeHeight: false,
        resetGrid: false,
        saveGrid: false,
        sideBar: false,
        exportBtn: false
      }}
      height="550px"
    />
  );

  if (isPageLoading) {
    return (
      <Row className="g-3">
        <Col lg={5} xs={12}>
          <ClusteringFormSkeleton />
        </Col>
        <Col lg={7} xs={12}>
          <Card>
            <Card.Body className="p-2">{tableSkeleton}</Card.Body>
          </Card>
        </Col>
      </Row>
    );
  }

  return (
    <Row className="g-3">
      <Col lg={5} xs={12}>
        <Card>
          <Card.Body>
            <Formik<ClusteringFormValues>
              onSubmit={onSubmit}
              initialValues={{
                words: []
              }}
              validationSchema={object().shape({
                words: array()
                  .of(string())
                  .test(
                    'min-keywords',
                    'حداقل باید ۵۰ کلمه وارد بشه.',
                    value => countKeywords(value) >= MIN_CLUSTERING_KEYWORDS
                  )
                  .test(
                    'max-keywords',
                    'تعداد کلمات بیش از حد مجاز است.',
                    value => countKeywords(value) <= LIMIT_CLUSTERING_KEYWORDS
                  )
              })}
            >
              {({
                values,
                errors,
                touched,
                handleSubmit,
                setFieldValue
              }) => {
                const wordCount = countKeywords(values.words);
                const remaining = LIMIT_CLUSTERING_KEYWORDS - wordCount;
                const overLimit = wordCount > LIMIT_CLUSTERING_KEYWORDS;
                const belowMin = wordCount < MIN_CLUSTERING_KEYWORDS;
                const usageRatio = wordCount / LIMIT_CLUSTERING_KEYWORDS;
                const progressTone = overLimit
                  ? 'danger'
                  : belowMin
                    ? 'warning'
                    : usageRatio > 0.85
                      ? 'warning'
                      : 'success';
                const wordsError = overLimit
                  ? 'تعداد کلمات بیش از حد مجاز است.'
                  : belowMin && (wordCount > 0 || touched.words)
                    ? 'حداقل باید ۵۰ کلمه وارد بشه.'
                    : null;

                return (
                  <Form onSubmit={handleSubmit}>
                    <div>
                      <Form.Label className="fs--2 mb-2 d-flex align-items-center gap-1 text-600">
                        هر کلمه رو در یک
                        <span className="fw-bold text-body">خط جدید</span>
                        بنویس
                        <HelpTooltip
                          html={keywordResearchTooltips.clustering.helper}
                        />
                      </Form.Label>
                      <Form.Control
                        as="textarea"
                        isInvalid={Boolean(wordsError)}
                        isValid={Boolean(!belowMin && !overLimit && wordCount > 0)}
                        rows={10}
                        size="sm"
                        type="text"
                        placeholder="کلمات مدنظرت رو وارد کن (هر خط یک کلمه)"
                        name="words"
                        onChange={e => {
                          setFieldValue('words', e.target.value.split('\n'));
                        }}
                        value={values.words.join('\n')}
                      />
                    </div>

                    <div
                      className="d-flex flex-column mt-3"
                      style={{ gap: 12 }}
                    >
                      <div>
                        <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 fs--2">
                          <div className="d-flex align-items-center gap-1 text-600">
                            {overLimit ? (
                              <>
                                <FontAwesomeIcon
                                  icon="exclamation-circle"
                                  className="text-danger"
                                />
                                <span className="text-danger">
                                  {numberHumanizer(Math.abs(remaining))} کلمه
                                  اضافه وارد کردی
                                </span>
                              </>
                            ) : (
                              <span>
                                {numberHumanizer(remaining)} کلمه دیگر می‌توانی
                                وارد کنی
                              </span>
                            )}
                          </div>
                          <span
                            className={classNames({
                              'text-danger': belowMin || overLimit,
                              'text-500': !belowMin && !overLimit
                            })}
                          >
                            حداقل {MIN_CLUSTERING_KEYWORDS} · حداکثر{' '}
                            {numberHumanizer(LIMIT_CLUSTERING_KEYWORDS)}
                          </span>
                        </div>
                        <div
                          className="progress mt-2 mb-0"
                          style={{ height: 4, borderRadius: 999 }}
                        >
                          <div
                            className={`progress-bar bg-${progressTone}`}
                            role="progressbar"
                            style={{
                              width: `${Math.min(
                                100,
                                Math.max(0, usageRatio * 100)
                              )}%`,
                              borderRadius: 999
                            }}
                            aria-valuenow={wordCount}
                            aria-valuemin={0}
                            aria-valuemax={LIMIT_CLUSTERING_KEYWORDS}
                          />
                        </div>
                        {wordsError ? (
                          <Form.Label className="text-danger m-0 mt-1 fs--1">
                            {wordsError}
                          </Form.Label>
                        ) : null}
                      </div>

                      <TypedClusteringModeSelector
                        mode={mode}
                        onChange={handleModeChange}
                      />

                      {mode === 'ai' ? (
                        <AiNoActiveApiKeyAlert
                          show={showNoActiveApiKeyAlert}
                          onClose={() => setShowNoActiveApiKeyAlert(false)}
                        />
                      ) : null}
                      <AiFormWarningAlert
                        message={formWarningMessage}
                        onClose={() => setFormWarningMessage(null)}
                      />

                      {mode === 'ai' && isFetchingAiSettings ? (
                        <Alert
                          variant="secondary"
                          className="mb-0 py-2 px-2 d-flex align-items-center gap-2 w-100"
                        >
                          <Spinner
                            style={{ width: '14px', height: '14px' }}
                          />
                          <span className="fs--1">
                            در حال بارگذاری تنظیمات AI...
                          </span>
                        </Alert>
                      ) : null}

                      {isCheckingWalletBalance ? (
                        <Alert
                          variant="secondary"
                          className="mb-0 py-2 px-2 d-flex align-items-center gap-2 w-100"
                        >
                          <Spinner
                            style={{ width: '14px', height: '14px' }}
                          />
                          <span className="fs--1">
                            در حال بررسی موجودی کیف پول AI...
                          </span>
                        </Alert>
                      ) : null}

                      {mode === 'ai' &&
                      usesBineshWallet &&
                      !needsApiKeySetup &&
                      !isCheckingWalletBalance &&
                      (isWalletBalanceError || !hasEnoughAiBalance) ? (
                        <Alert
                          variant="danger"
                          className="mb-0 py-2 px-2 d-flex align-items-start gap-2 w-100 overflow-hidden"
                        >
                          <FontAwesomeIcon
                            icon="exclamation-circle"
                            className="flex-shrink-0 mt-1"
                          />
                          <span className="fs--1 text-break min-w-0">
                            برای استفاده از خوشه‌بندی با AI، کیف پول AI شما باید
                            حداقل ۱۰۰ هزار تومان شارژ داشته باشد.
                          </span>
                        </Alert>
                      ) : null}

                      {isAiConsentRequired ? (
                        <Alert
                          variant="warning"
                          className="mb-0 py-2 px-2 w-100 overflow-hidden"
                        >
                          <Form.Check className="mb-0 d-flex align-items-start gap-2">
                            <Form.Check.Input
                              type="checkbox"
                              id="ai-clustering-consent"
                              className="flex-shrink-0 mt-1"
                              checked={aiConsent}
                              onChange={e =>
                                handleAiConsentChange(e.target.checked)
                              }
                            />
                            <Form.Check.Label
                              htmlFor="ai-clustering-consent"
                              className="fs--1 text-break min-w-0 mb-0"
                            >
                              هزینه خوشه‌بندی با AI بسته به تعداد کلمات، معمولاً
                              بین ۱۰ تا ۵۰ هزار تومان از اعتبار AI شما کسر
                              می‌شود. با این موضوع موافقم و ادامه می‌دهم.
                            </Form.Check.Label>
                          </Form.Check>
                        </Alert>
                      ) : null}

                      {isAiConsentRequired && showAiConsentError ? (
                        <Alert
                          variant="danger"
                          className="mb-0 py-2 px-2 d-flex align-items-start gap-2 w-100 overflow-hidden"
                        >
                          <FontAwesomeIcon
                            icon="exclamation-circle"
                            className="flex-shrink-0 mt-1"
                          />
                          <span className="fs--1 text-break min-w-0">
                            برای ادامه، ابتدا تیک موافقت با کسر هزینه از اعتبار
                            AI را بزنید.
                          </span>
                        </Alert>
                      ) : null}

                      <ChildrensTooltip
                        placement="top"
                        className="p-0"
                        html={
                          !isAccessAllowed()
                            ? 'شما دسترسی لازم را ندارید'
                            : mode === 'ai' && needsApiKeySetup
                              ? 'کلید API شخصی فعال ندارید. ابتدا در تنظیمات AI یک کلید ثبت یا فعال کنید.'
                              : isCheckingWalletBalance
                                ? 'در حال بررسی موجودی کیف پول AI...'
                                : mode === 'ai' &&
                                    usesBineshWallet &&
                                    (isWalletBalanceError || !hasEnoughAiBalance)
                                  ? 'برای استفاده از خوشه‌بندی با AI، کیف پول AI شما باید حداقل ۱۰۰ هزار تومان شارژ داشته باشد.'
                                  : ''
                        }
                      >
                        <Button
                          disabled={
                            !isAccessAllowed() ||
                            isCreating ||
                            overLimit ||
                            belowMin ||
                            isAiModeBlocked
                          }
                          variant="falcon-primary"
                          type="submit"
                          className="w-100 mb-auto"
                        >
                          {isCreating ? (
                            <>
                              <Spinner
                                style={{
                                  width: '16px',
                                  height: '16px'
                                }}
                              />
                              درحال خوشه‌بندی
                            </>
                          ) : (
                            <>
                              <FontAwesomeIcon
                                icon="search"
                                className="mx-1 fs--1"
                              />
                              خوشه‌بندی کن{' '}
                              {wordCount > 0
                                ? ` (${numberHumanizer(wordCount).toPersianDigit()}) `
                                : ''}
                            </>
                          )}
                        </Button>
                      </ChildrensTooltip>
                    </div>
                  </Form>
                );
              }}
            </Formik>
          </Card.Body>
        </Card>
      </Col>
      <Col lg={7} xs={12}>
        <Card>
          <Card.Body className="p-2">
            <div className="for-style" ref={gridRef}>
              <TypedAgGridWrapper
                settings={{
                  pagination: false,
                  fullscreen: false,
                  changeHeight: false,
                  resetGrid: false,
                  saveGrid: false,
                  sideBar: true,
                  exportBtn: false
                }}
                height="550px"
                columns={clusteringHistoryColumnConfig}
                data={data?.data ? data.data : []}
              />
              <style>{`.for-style .ag-row-focus { z-index: 2; }`}</style>
            </div>
          </Card.Body>
        </Card>
      </Col>
    </Row>
  );
};

export default Clustering;
